import React, { useState, useEffect } from 'react';
import { CmrArticle2OtherModeType, ECMR, LogisticsLocation, LogisticsServiceCharge, Note, TradeParty, TransportEvent } from '../types/eCMR';
import { getStandardCodes } from '../services/schemaValidator';
import { ISO_4217_CHARGE_CURRENCY_CODES } from '../compliance/iso4217ChargeCurrencyCodes';

interface ECMRFormProps {
  initialData: ECMR;
  onSubmit: (data: ECMR) => void;
  onCancel: () => void;
  disabled?: boolean;
}

const CMR_CONVENTION_STATEMENT_CONTENT = 'Carriage is subject to the CMR Convention provisions.';
const CMR_CONVENTION_STATEMENT_SUBJECT_CODE = 'CMR_ARTICLE_6_1';

const cmrConventionStatementNote = (): Note => ({
  Content: [CMR_CONVENTION_STATEMENT_CONTENT],
  SubjectCode: [CMR_CONVENTION_STATEMENT_SUBJECT_CODE],
});

const noteHasAffirmativeCmrConventionStatement = (note?: Note): boolean => {
  const text = [note?.Subject, ...(note?.Content || [])].filter(Boolean).join(' ');

  return (
    /\bCMR\b/i.test(text) &&
    /\b(?:Convention|provisions)\b/i.test(text) &&
    /\bsubject\b/i.test(text) &&
    !/\b(?:not|never)\s+(?:be\s+)?subject\b|\bnot\s+governed\b|\bnot\s+covered\b|\bsubject\s+to\s+no\b/i.test(text)
  );
};

const ensureCmrConventionStatement = (document: ECMR): ECMR => {
  const includedNotes = document.ExchangedDocument?.IncludedNote || [];

  if (includedNotes.some(noteHasAffirmativeCmrConventionStatement)) {
    return document;
  }

  return {
    ...document,
    ExchangedDocument: {
      ...document.ExchangedDocument,
      IncludedNote: [cmrConventionStatementNote(), ...includedNotes],
    },
  };
};

const normalizeChargeCurrencyCode = (value: string | undefined): string => {
  const normalized = (value || '').trim().toUpperCase();

  return normalized || 'EUR';
};

type ConsignmentLocationField = 'CarrierAcceptanceLogisticsLocation' | 'ConsigneeReceiptLogisticsLocation';
type ConsignmentEventField = 'PickUpTransportEvent' | 'DeliveryTransportEvent';
type ConsignmentPartyField = 'ConsignorTradeParty' | 'ConsigneeTradeParty' | 'CarrierTradeParty';

const partyEmailValue = (party?: TradeParty): string => {
  const value = party?.DefinedTradeContact?.[0]?.EmailURIUniversalCommunication?.URIID;
  return value?.trim().replace(/^mailto:/i, '') || '';
};

const ECMRForm: React.FC<ECMRFormProps> = ({ initialData, onSubmit, onCancel, disabled = false }) => {
  const [formData, setFormData] = useState<ECMR>(() => ensureCmrConventionStatement(initialData));
  const [invoiceCurrencyDraft, setInvoiceCurrencyDraft] = useState<string>(() => normalizeChargeCurrencyCode(
    initialData.SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount?.[0]?.currencyID
  ));
  const [transportChargeCurrencyDraft, setTransportChargeCurrencyDraft] = useState<string>(() => normalizeChargeCurrencyCode(
    initialData.SpecifiedSupplyChainConsignment.ApplicableLogisticsServiceCharge?.[0]?.AppliedAmount?.[0]?.currencyID ||
      initialData.SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount?.[0]?.currencyID
  ));
  const [countryCodes, setCountryCodes] = useState<{ code: string, description: string }[]>([]);
  const [transportModeCodes, setTransportModeCodes] = useState<{ code: string, description: string }[]>([]);
  const [packageTypeCodes, setPackageTypeCodes] = useState<{ code: string, description: string }[]>([]);
  const [deliveryTermsCodes, setDeliveryTermsCodes] = useState<{ code: string, description: string }[]>([]);
  
  useEffect(() => {
    // Cargar los códigos estándar
    setCountryCodes(getStandardCodes('CountryCode'));
    setTransportModeCodes(getStandardCodes('TransportModeCode'));
    setPackageTypeCodes(getStandardCodes('PackageTypeCode'));
    setDeliveryTermsCodes(getStandardCodes('DeliveryTermsCode'));
  }, []);
  
  const handleChange = (section: keyof ECMR, field: string, value: any) => {
    if (section === 'ExchangedDocument') {
      setFormData({
        ...formData,
        ExchangedDocument: {
          ...formData.ExchangedDocument,
          [field]: value
        }
      });
    } else if (section === 'SpecifiedSupplyChainConsignment') {
      setFormData({
        ...formData,
        SpecifiedSupplyChainConsignment: {
          ...formData.SpecifiedSupplyChainConsignment,
          [field]: value
        }
      });
    }
  };
  
  const handlePartyAddressChange = (partyType: ConsignmentPartyField, field: string, value: any) => {
    setFormData({
      ...formData,
      SpecifiedSupplyChainConsignment: {
        ...formData.SpecifiedSupplyChainConsignment,
        [partyType]: {
          ...formData.SpecifiedSupplyChainConsignment[partyType],
          PostalTradeAddress: {
            ...formData.SpecifiedSupplyChainConsignment[partyType]?.PostalTradeAddress,
            [field]: value
          }
        }
      }
    });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateConsignment = (updates: Partial<ECMR['SpecifiedSupplyChainConsignment']>) => {
    setFormData({
      ...formData,
      SpecifiedSupplyChainConsignment: {
        ...formData.SpecifiedSupplyChainConsignment,
        ...updates,
      },
    });
  };

  const handlePartyEmailChange = (partyType: ConsignmentPartyField, value: string) => {
    const party = formData.SpecifiedSupplyChainConsignment[partyType] || {};
    const contacts = [...(party.DefinedTradeContact || [])];
    const primaryContact = contacts[0] || {};
    contacts[0] = {
      ...primaryContact,
      EmailURIUniversalCommunication: {
        ...primaryContact.EmailURIUniversalCommunication,
        URIID: value,
      },
    };
    setFormData({
      ...formData,
      SpecifiedSupplyChainConsignment: {
        ...formData.SpecifiedSupplyChainConsignment,
        [partyType]: {
          ...party,
          DefinedTradeContact: contacts,
        },
      },
    });
  };

  const updateCmrConventionStatement = (enabled: boolean) => {
    const includedNotes = formData.ExchangedDocument?.IncludedNote || [];
    const nextNotes = enabled
      ? includedNotes.some(noteHasAffirmativeCmrConventionStatement)
        ? includedNotes
        : [cmrConventionStatementNote(), ...includedNotes]
      : includedNotes.filter((note) => !noteHasAffirmativeCmrConventionStatement(note));

    setFormData({
      ...formData,
      ExchangedDocument: {
        ...formData.ExchangedDocument,
        IncludedNote: nextNotes,
      },
    });
  };

  const updateConsignmentLocation = (
    field: ConsignmentLocationField,
    updates: Partial<LogisticsLocation>
  ) => {
    const existingLocation = formData.SpecifiedSupplyChainConsignment[field] || {};

    updateConsignment({
      [field]: {
        ...existingLocation,
        ...updates,
      },
    });
  };

  const updateConsignmentLocationCountry = (field: ConsignmentLocationField, countryId: string) => {
    const existingLocation = formData.SpecifiedSupplyChainConsignment[field] || {};

    updateConsignment({
      [field]: {
        ...existingLocation,
        PostalTradeAddress: {
          ...existingLocation.PostalTradeAddress,
          CountryID: countryId,
        },
      },
    });
  };

  const updateConsignmentEventDate = (field: ConsignmentEventField, value: string) => {
    const existingEvent = formData.SpecifiedSupplyChainConsignment[field] || {};

    updateConsignment({
      [field]: {
        ...(existingEvent as TransportEvent),
        ScheduledOccurrenceDateTime: value ? { DateTimeString: value } : undefined,
      },
    });
  };

  const updateFirstConsignmentItem = (updates: any) => {
    const existingItems = formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem || [];
    const firstItem = existingItems[0] || { SequenceNumeric: 1 };

    updateConsignment({
      IncludedSupplyChainConsignmentItem: [
        {
          ...firstItem,
          ...updates,
        },
        ...existingItems.slice(1),
      ],
    });
  };

  const updateFirstPackage = (updates: any) => {
    const existingItems = formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem || [];
    const firstItem = existingItems[0] || { SequenceNumeric: 1 };
    const existingPackages = firstItem.TransportLogisticsPackage || [];
    const firstPackage = existingPackages[0] || { ItemQuantity: 1 };

    updateFirstConsignmentItem({
      TransportLogisticsPackage: [
        {
          ...firstPackage,
          ...updates,
        },
        ...existingPackages.slice(1),
      ],
    });
  };

  const updateFirstTransportMovement = (updates: any) => {
    const existingMovements = formData.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement || [];
    const firstMovement = existingMovements[0] || {};

    updateConsignment({
      MainCarriageLogisticsTransportMovement: [
        {
          ...firstMovement,
          ...updates,
        },
        ...existingMovements.slice(1),
      ],
    });
  };

  const invoiceCurrency = normalizeChargeCurrencyCode(
    formData.SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount?.[0]?.currencyID || invoiceCurrencyDraft
  );
  const firstLogisticsServiceCharge = formData.SpecifiedSupplyChainConsignment.ApplicableLogisticsServiceCharge?.[0];
  const transportChargeAmount = firstLogisticsServiceCharge?.AppliedAmount?.[0]?.value || '';
  const transportChargeCurrency = normalizeChargeCurrencyCode(
    firstLogisticsServiceCharge?.AppliedAmount?.[0]?.currencyID || transportChargeCurrencyDraft || invoiceCurrency
  );

  const upsertFirstLogisticsServiceCharge = (updates: Partial<LogisticsServiceCharge>) => {
    const existingCharges = formData.SpecifiedSupplyChainConsignment.ApplicableLogisticsServiceCharge || [];
    const existingCharge = existingCharges[0] || {};

    updateConsignment({
      ApplicableLogisticsServiceCharge: [
        {
          PaymentArrangementCode: existingCharge.PaymentArrangementCode || 'P',
          CategoryCode: existingCharge.CategoryCode || '2',
          ...existingCharge,
          ...updates,
        },
        ...existingCharges.slice(1),
      ],
    });
  };

  const updateTransportChargeAmount = (value: string) => {
    if (!value) {
      updateConsignment({ ApplicableLogisticsServiceCharge: [] });
      return;
    }

    upsertFirstLogisticsServiceCharge({
      AppliedAmount: [{ value, currencyID: transportChargeCurrency }],
    });
  };

  const updateTransportChargeCurrency = (currencyID: string) => {
    const normalizedCurrencyID = normalizeChargeCurrencyCode(currencyID);
    setTransportChargeCurrencyDraft(normalizedCurrencyID);

    if (!transportChargeAmount) {
      return;
    }

    upsertFirstLogisticsServiceCharge({
      AppliedAmount: [{ value: transportChargeAmount, currencyID: normalizedCurrencyID }],
    });
  };

  const updateInvoiceCurrency = (currencyID: string) => {
    const normalizedCurrencyID = normalizeChargeCurrencyCode(currencyID);
    setInvoiceCurrencyDraft(normalizedCurrencyID);

    updateConsignment({
      AssociatedInvoiceAmount: formData.SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount?.[0]?.value
        ? [{ value: formData.SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount[0].value, currencyID: normalizedCurrencyID }]
        : [],
      CODAmount: formData.SpecifiedSupplyChainConsignment.CODAmount?.value
        ? { ...formData.SpecifiedSupplyChainConsignment.CODAmount, currencyID: normalizedCurrencyID }
        : undefined,
    });
  };

  const updateOptionalApplicability = (
    key: keyof NonNullable<ECMR['SpecifiedSupplyChainConsignment']['CmrOptionalParticularsApplicable']>,
    value: boolean
  ) => {
    const updates: Partial<ECMR['SpecifiedSupplyChainConsignment']> = {
      CmrOptionalParticularsApplicable: {
        ...(formData.SpecifiedSupplyChainConsignment.CmrOptionalParticularsApplicable || {}),
        [key]: value,
      },
    };

    if (key === 'senderPaidCharges' && value && firstLogisticsServiceCharge) {
      updates.ApplicableLogisticsServiceCharge = [
        {
          ...firstLogisticsServiceCharge,
          PaymentArrangementCode: firstLogisticsServiceCharge.PaymentArrangementCode || 'P',
          CategoryCode: firstLogisticsServiceCharge.CategoryCode || '2',
          PayingPartyRoleCode: 'CZ',
        },
        ...(formData.SpecifiedSupplyChainConsignment.ApplicableLogisticsServiceCharge || []).slice(1),
      ];
    }

    updateConsignment(updates);
  };

  const updateSenderPaidChargeAmount = (value: string) => {
    if (!value) {
      updateConsignment({ ApplicableLogisticsServiceCharge: [] });
      return;
    }

    upsertFirstLogisticsServiceCharge({
      PayingPartyRoleCode: 'CZ',
      AppliedAmount: [{ value, currencyID: transportChargeCurrency }],
    });
  };

  const optionalApplicability = formData.SpecifiedSupplyChainConsignment.CmrOptionalParticularsApplicable || {};
  const article2Multimodal = formData.SpecifiedSupplyChainConsignment.CmrArticle2MultimodalCarriage || {};

  const updateArticle2Multimodal = (
    updates: Partial<ECMR['SpecifiedSupplyChainConsignment']['CmrArticle2MultimodalCarriage']>
  ) => {
    const next = {
      ...article2Multimodal,
      ...updates,
    };

    if (next.vehicleCarriedByOtherMode !== true) {
      updateConsignment({ CmrArticle2MultimodalCarriage: undefined });
      return;
    }

    updateConsignment({
      CmrArticle2MultimodalCarriage: {
        vehicleCarriedByOtherMode: true,
        ...(next.otherModeType ? { otherModeType: next.otherModeType } : {}),
        goodsUnloadedFromVehicle: next.goodsUnloadedFromVehicle === true,
        article14ImpedimentBasis: next.article14ImpedimentBasis === true,
      },
    });
  };

  // Función para renderizar selector de códigos estándar
  const renderCodeSelector = (
    label: string, 
    value: string | undefined, 
    onChange: (value: string) => void, 
    options: { code: string, description: string }[],
    required: boolean = false
  ) => {
    return (
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required={required}
        >
          <option value="">Seleccionar...</option>
          {options.map((option) => (
            <option key={option.code} value={option.code}>
              {option.code} - {option.description}
            </option>
          ))}
        </select>
      </div>
    );
  };
  
  return (
    <div className="container mx-auto p-4">
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded p-6">
        {/* Información básica del documento */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Información del Documento</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="documentId">
                ID del Documento <span className="text-red-500">*</span>
              </label>
              <input
                id="documentId"
                type="text"
                value={formData.ExchangedDocument?.ID || ''}
                onChange={(e) => handleChange('ExchangedDocument', 'ID', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="issueDateTime">
                Fecha de Emisión <span className="text-red-500">*</span>
              </label>
              <input
                id="issueDateTime"
                type="text"
                value={formData.ExchangedDocument?.IssueDateTime?.DateTimeString || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ExchangedDocument: {
                      ...formData.ExchangedDocument,
                      IssueDateTime: {
                        DateTimeString: e.target.value,
                      },
                    },
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="issueLocation">
                Lugar de Emisión <span className="text-red-500">*</span>
              </label>
              <input
                id="issueLocation"
                type="text"
                value={formData.ExchangedDocument?.IssueLogisticsLocation?.Name || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ExchangedDocument: {
                      ...formData.ExchangedDocument,
                      IssueLogisticsLocation: {
                        ...formData.ExchangedDocument?.IssueLogisticsLocation,
                        Name: e.target.value
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="remarks">
                Observaciones
              </label>
              <input
                id="remarks"
                type="text"
                value={formData.ExchangedDocument?.Remarks?.[0] || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ExchangedDocument: {
                      ...formData.ExchangedDocument,
                      Remarks: [e.target.value]
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4 col-span-2">
              <label className="inline-flex items-center gap-2 text-gray-700 text-sm font-bold" htmlFor="cmrConventionStatement">
                <input
                  id="cmrConventionStatement"
                  type="checkbox"
                  checked={formData.ExchangedDocument?.IncludedNote?.some(noteHasAffirmativeCmrConventionStatement) || false}
                  onChange={(e) => updateCmrConventionStatement(e.target.checked)}
                  disabled={disabled}
                  className="h-4 w-4"
                />
                Transporte sujeto al Convenio CMR
              </label>
            </div>
          </div>
        </div>
        
        {/* Información del Remitente */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Toma y Entrega</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pickupLocationName">
                Lugar de toma <span className="text-red-500">*</span>
              </label>
              <input
                id="pickupLocationName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierAcceptanceLogisticsLocation?.Name || ''}
                onChange={(e) => updateConsignmentLocation('CarrierAcceptanceLogisticsLocation', { Name: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pickupCountryId">
                País de toma <span className="text-red-500">*</span>
              </label>
              <input
                id="pickupCountryId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierAcceptanceLogisticsLocation?.PostalTradeAddress?.CountryID || ''}
                onChange={(e) => updateConsignmentLocationCountry('CarrierAcceptanceLogisticsLocation', e.target.value.toUpperCase())}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pickupDateTime">
                Fecha de toma <span className="text-red-500">*</span>
              </label>
              <input
                id="pickupDateTime"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.PickUpTransportEvent?.ScheduledOccurrenceDateTime?.DateTimeString || ''}
                onChange={(e) => updateConsignmentEventDate('PickUpTransportEvent', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deliveryLocationName">
                Lugar de entrega <span className="text-red-500">*</span>
              </label>
              <input
                id="deliveryLocationName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeReceiptLogisticsLocation?.Name || ''}
                onChange={(e) => updateConsignmentLocation('ConsigneeReceiptLogisticsLocation', { Name: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deliveryCountryId">
                País de entrega <span className="text-red-500">*</span>
              </label>
              <input
                id="deliveryCountryId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeReceiptLogisticsLocation?.PostalTradeAddress?.CountryID || ''}
                onChange={(e) => updateConsignmentLocationCountry('ConsigneeReceiptLogisticsLocation', e.target.value.toUpperCase())}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="deliveryDateTime">
                Fecha de entrega <span className="text-red-500">*</span>
              </label>
              <input
                id="deliveryDateTime"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.DeliveryTransportEvent?.ScheduledOccurrenceDateTime?.DateTimeString || ''}
                onChange={(e) => updateConsignmentEventDate('DeliveryTransportEvent', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Remitente</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorName">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.Name?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsignorTradeParty: {
                        ...party,
                        Name: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorId">
                ID/NIF <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.ID?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsignorTradeParty: {
                        ...party,
                        ID: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorEmail">
                Email del remitente <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorEmail"
                type="email"
                value={partyEmailValue(formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty)}
                onChange={(e) => handlePartyEmailChange('ConsignorTradeParty', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorStreet">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorStreet"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.StreetName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsignorTradeParty', 'StreetName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorCity">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorCity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.CityName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsignorTradeParty', 'CityName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignorPostcode">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                id="consignorPostcode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.PostcodeCode || ''}
                onChange={(e) => handlePartyAddressChange('ConsignorTradeParty', 'PostcodeCode', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Selector de país para el remitente */}
            {renderCodeSelector(
              'País',
              formData.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.CountryID,
              (value) => handlePartyAddressChange('ConsignorTradeParty', 'CountryID', value),
              countryCodes,
              true
            )}
          </div>
        </div>
        
        {/* Información del Destinatario */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Destinatario</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeName">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.Name?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsigneeTradeParty: {
                        ...party,
                        Name: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeId">
                ID/NIF <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.ID?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      ConsigneeTradeParty: {
                        ...party,
                        ID: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Dirección del destinatario (similar al remitente) */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeEmail">
                Email del destinatario
              </label>
              <input
                id="consigneeEmail"
                type="email"
                value={partyEmailValue(formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty)}
                onChange={(e) => handlePartyEmailChange('ConsigneeTradeParty', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeStreet">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeStreet"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.StreetName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsigneeTradeParty', 'StreetName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneeCity">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneeCity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.CityName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('ConsigneeTradeParty', 'CityName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consigneePostcode">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                id="consigneePostcode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.PostcodeCode || ''}
                onChange={(e) => handlePartyAddressChange('ConsigneeTradeParty', 'PostcodeCode', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Selector de país para el destinatario */}
            {renderCodeSelector(
              'País',
              formData.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.CountryID,
              (value) => handlePartyAddressChange('ConsigneeTradeParty', 'CountryID', value),
              countryCodes,
              true
            )}
          </div>
        </div>
        
        {/* Información del Transportista */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Transportista</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierName">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.Name?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.CarrierTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      CarrierTradeParty: {
                        ...party,
                        Name: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierId">
                ID/NIF <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierId"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.ID?.[0] || ''}
                onChange={(e) => {
                  const party = formData.SpecifiedSupplyChainConsignment.CarrierTradeParty || {};
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      CarrierTradeParty: {
                        ...party,
                        ID: [e.target.value]
                      }
                    }
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierEmail">
                Email del transportista <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierEmail"
                type="email"
                value={partyEmailValue(formData.SpecifiedSupplyChainConsignment.CarrierTradeParty)}
                onChange={(e) => handlePartyEmailChange('CarrierTradeParty', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierStreet">
                Dirección <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierStreet"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.StreetName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('CarrierTradeParty', 'StreetName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierCity">
                Ciudad <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierCity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.CityName?.[0] || ''}
                onChange={(e) => handlePartyAddressChange('CarrierTradeParty', 'CityName', [e.target.value])}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierPostcode">
                Código Postal <span className="text-red-500">*</span>
              </label>
              <input
                id="carrierPostcode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.PostcodeCode || ''}
                onChange={(e) => handlePartyAddressChange('CarrierTradeParty', 'PostcodeCode', e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            
            {/* Selector de país para el transportista */}
            {renderCodeSelector(
              'País',
              formData.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.CountryID,
              (value) => handlePartyAddressChange('CarrierTradeParty', 'CountryID', value),
              countryCodes,
              true
            )}
          </div>
        </div>
        
        {/* Información de Mercancía y Embalaje */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Mercancía y Embalaje</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="invoiceAmount">
                Importe de factura
              </label>
              <input
                id="invoiceAmount"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.AssociatedInvoiceAmount?.[0]?.value || ''}
                onChange={(e) => updateConsignment({
                  AssociatedInvoiceAmount: e.target.value ? [{ value: e.target.value, currencyID: invoiceCurrency }] : [],
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="transportChargeAmount">
                Cargos de transporte
              </label>
              <input
                id="transportChargeAmount"
                type="text"
                value={transportChargeAmount}
                onChange={(e) => updateTransportChargeAmount(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="transportChargeCurrency">
                Moneda de cargos de transporte
              </label>
              <select
                id="transportChargeCurrency"
                value={transportChargeCurrency}
                onChange={(e) => updateTransportChargeCurrency(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {ISO_4217_CHARGE_CURRENCY_CODES.map((currencyCode) => (
                  <option key={currencyCode} value={currencyCode}>
                    {currencyCode}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="invoiceCurrency">
                Moneda de factura
              </label>
              <select
                id="invoiceCurrency"
                value={invoiceCurrency}
                onChange={(e) => updateInvoiceCurrency(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {ISO_4217_CHARGE_CURRENCY_CODES.map((currencyCode) => (
                  <option key={currencyCode} value={currencyCode}>
                    {currencyCode}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="codAmount">
                Importe contra reembolso
              </label>
              <input
                id="codAmount"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CODAmount?.value || ''}
                onChange={(e) => updateConsignment({
                  CODAmount: e.target.value ? { value: e.target.value, currencyID: invoiceCurrency } : undefined,
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="consignmentItemQuantity">
                Cantidad total de bultos
              </label>
              <input
                id="consignmentItemQuantity"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignmentItemQuantity || ''}
                onChange={(e) => updateConsignment({ ConsignmentItemQuantity: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="grossWeightMeasure">
                Peso bruto
              </label>
              <input
                id="grossWeightMeasure"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.GrossWeightMeasure?.[0]?.value || ''}
                onChange={(e) => updateConsignment({
                  GrossWeightMeasure: e.target.value ? [{ value: e.target.value }] : [],
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="insuranceInstructions">
                Instrucciones de seguro
              </label>
              <input
                id="insuranceInstructions"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.CargoInsuranceInstructionsInformation || ''}
                onChange={(e) => updateConsignment({ CargoInsuranceInstructionsInformation: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="customsInstructions">
                Instrucciones aduaneras
              </label>
              <input
                id="customsInstructions"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.ConsignorProvidedBorderClearanceTransportInstructions?.[0]?.Description || ''}
                onChange={(e) => updateConsignment({
                  ConsignorProvidedBorderClearanceTransportInstructions: e.target.value
                    ? [{ Description: e.target.value, DescriptionCode: 'CUS' }]
                    : [],
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cargoNature">
                Naturaleza de la mercancía
              </label>
              <input
                id="cargoNature"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.[0]?.NatureIdentificationTransportCargo?.Identification?.[0] || ''}
                onChange={(e) => updateFirstConsignmentItem({
                  NatureIdentificationTransportCargo: { Identification: e.target.value ? [e.target.value] : [] },
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="dangerousGoodsCode">
                Código UN mercancía peligrosa
              </label>
              <input
                id="dangerousGoodsCode"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.[0]?.ApplicableTransportDangerousGoods?.[0]?.UNDGIdentificationCode || ''}
                onChange={(e) => updateFirstConsignmentItem({
                  ApplicableTransportDangerousGoods: e.target.value ? [{
                    ...(formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.[0]?.ApplicableTransportDangerousGoods?.[0] || {}),
                    UNDGIdentificationCode: e.target.value,
                  }] : [],
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="dangerousGoodsTechnicalName">
                Nombre técnico mercancía peligrosa
              </label>
              <input
                id="dangerousGoodsTechnicalName"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.[0]?.ApplicableTransportDangerousGoods?.[0]?.TechnicalName?.[0] || ''}
                onChange={(e) => {
                  const existingDangerousGoods = formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.[0]?.ApplicableTransportDangerousGoods?.[0];
                  updateFirstConsignmentItem({
                    ApplicableTransportDangerousGoods: existingDangerousGoods?.UNDGIdentificationCode
                      ? [{ ...existingDangerousGoods, TechnicalName: e.target.value ? [e.target.value] : [] }]
                      : [],
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="shippingMarks">
                Marcas y números
              </label>
              <input
                id="shippingMarks"
                type="text"
                value={formData.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.[0]?.TransportLogisticsPackage?.[0]?.PhysicalLogisticsShippingMarks?.[0]?.Marking?.[0] || ''}
                onChange={(e) => updateFirstPackage({
                  PhysicalLogisticsShippingMarks: e.target.value ? [{ Marking: [e.target.value] }] : [],
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
          </div>

          <div className="mb-6 border rounded p-4 bg-gray-50">
            <h3 className="text-lg font-semibold mb-3">Particulares opcionales CMR 6.2 aplicables</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.transshipmentProhibition || false}
                  onChange={(e) => {
                    updateConsignment({
                      CmrOptionalParticularsApplicable: {
                        ...optionalApplicability,
                        transshipmentProhibition: e.target.checked,
                      },
                      TransshipmentPermissionIndicator: e.target.checked ? false : undefined,
                    });
                  }}
                  className="mr-2"
                />
                Aplica prohibición de transbordo
              </label>

              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.senderPaidCharges || false}
                  onChange={(e) => updateOptionalApplicability('senderPaidCharges', e.target.checked)}
                  className="mr-2"
                />
                Aplican gastos pagados por el remitente
              </label>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="senderPaidChargeAmount">
                  Importe de gastos pagados por el remitente
                </label>
                <input
                  id="senderPaidChargeAmount"
                  type="text"
                  value={transportChargeAmount}
                  onChange={(e) => updateSenderPaidChargeAmount(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.cashOnDelivery || false}
                  onChange={(e) => updateOptionalApplicability('cashOnDelivery', e.target.checked)}
                  className="mr-2"
                />
                Aplica contra reembolso
              </label>

              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.declaredValueOrSpecialInterest || false}
                  onChange={(e) => updateOptionalApplicability('declaredValueOrSpecialInterest', e.target.checked)}
                  className="mr-2"
                />
                Aplica valor declarado o interés especial
              </label>

              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.insuranceInstructions || false}
                  onChange={(e) => updateOptionalApplicability('insuranceInstructions', e.target.checked)}
                  className="mr-2"
                />
                Marcar particular de seguro aplicable
              </label>

              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.agreedTimeLimit || false}
                  onChange={(e) => updateOptionalApplicability('agreedTimeLimit', e.target.checked)}
                  className="mr-2"
                />
                Marcar plazo de entrega como aplicable
              </label>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="agreedDeliveryTimeLimit">
                  Plazo de entrega acordado
                </label>
                <input
                  id="agreedDeliveryTimeLimit"
                  type="text"
                  value={formData.SpecifiedSupplyChainConsignment.DeliveryInstructions?.[0]?.Description?.[0] || ''}
                  onChange={(e) => updateConsignment({
                    DeliveryInstructions: e.target.value ? [{ Description: [e.target.value] }] : [],
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>

              <label className="flex items-center text-gray-700 text-sm font-bold">
                <input
                  type="checkbox"
                  checked={optionalApplicability.documentsHandedToCarrier || false}
                  onChange={(e) => updateOptionalApplicability('documentsHandedToCarrier', e.target.checked)}
                  className="mr-2"
                />
                Aplican documentos entregados al transportista
              </label>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="carrierHandedDocument">
                  Documento entregado al transportista
                </label>
                <input
                  id="carrierHandedDocument"
                  type="text"
                  value={formData.SpecifiedSupplyChainConsignment.AssociatedReferencedDocument?.[0]?.ID || ''}
                  onChange={(e) => updateConsignment({
                    AssociatedReferencedDocument: e.target.value
                      ? [{
                          ID: e.target.value,
                          TypeCode: '380',
                          Remarks: ['Document handed to carrier.'],
                        }]
                      : [],
                  })}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>
          </div>
          
          {/* CMR Article 2 multimodal carriage */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">CMR Article 2</h3>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mainTransportModeCode">
                Modo principal CMR
              </label>
              <select
                id="mainTransportModeCode"
                value={formData.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement?.[0]?.ModeCode || ''}
                onChange={(e) => updateFirstTransportMovement({ ModeCode: e.target.value || undefined })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Seleccionar...</option>
                {transportModeCodes.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.code} - {option.description}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center text-gray-700 text-sm font-bold mb-3">
              <input
                type="checkbox"
                checked={article2Multimodal.vehicleCarriedByOtherMode === true}
                onChange={(e) => updateArticle2Multimodal({ vehicleCarriedByOtherMode: e.target.checked })}
                className="mr-2"
              />
              Vehiculo transportado por otro modo no carretero
            </label>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="article2OtherModeType">
                Modo no carretero
              </label>
              <select
                id="article2OtherModeType"
                value={article2Multimodal.otherModeType || ''}
                onChange={(e) => updateArticle2Multimodal({
                  otherModeType: e.target.value ? e.target.value as CmrArticle2OtherModeType : undefined,
                })}
                disabled={article2Multimodal.vehicleCarriedByOtherMode !== true}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:bg-gray-100"
              >
                <option value="">Seleccionar...</option>
                <option value="sea">Mar</option>
                <option value="rail">Ferrocarril</option>
                <option value="inland_waterways">Vias navegables interiores</option>
                <option value="air">Aire</option>
              </select>
            </div>

            <label className="flex items-center text-gray-700 text-sm font-bold mb-3">
              <input
                type="checkbox"
                checked={article2Multimodal.goodsUnloadedFromVehicle === true}
                disabled={article2Multimodal.vehicleCarriedByOtherMode !== true}
                onChange={(e) => updateArticle2Multimodal({
                  goodsUnloadedFromVehicle: e.target.checked,
                  article14ImpedimentBasis: e.target.checked
                    ? article2Multimodal.article14ImpedimentBasis === true
                    : false,
                })}
                className="mr-2"
              />
              Mercancia descargada del vehiculo
            </label>

            <label className="flex items-center text-gray-700 text-sm font-bold">
              <input
                type="checkbox"
                checked={article2Multimodal.article14ImpedimentBasis === true}
                disabled={
                  article2Multimodal.vehicleCarriedByOtherMode !== true ||
                  article2Multimodal.goodsUnloadedFromVehicle !== true
                }
                onChange={(e) => updateArticle2Multimodal({ article14ImpedimentBasis: e.target.checked })}
                className="mr-2"
              />
              Existe base Article 14 para descarga por impedimento
            </label>
          </div>

          {/* Selectores para tipos de embalaje */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="packageTypeCode">
              Tipo de Embalaje
            </label>
            <select
              id="packageTypeCode"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              onChange={(e) => {
                const packageType = e.target.value;
                if (!packageType) return;

                updateFirstPackage({ TypeCode: packageType });
              }}
            >
              <option value="">Seleccionar...</option>
              {packageTypeCodes.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.code} - {option.description}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Términos de Entrega */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Términos de Entrega</h2>
          
          {/* Selector para términos de entrega (Incoterms) */}
          {renderCodeSelector(
            'Términos de Entrega (Incoterms)',
            formData.SpecifiedSupplyChainConsignment.SpecifiedTradeDeliveryTerms?.DeliveryTypeCode,
            (value) => {
              setFormData({
                ...formData,
                SpecifiedSupplyChainConsignment: {
                  ...formData.SpecifiedSupplyChainConsignment,
                  SpecifiedTradeDeliveryTerms: {
                    ...formData.SpecifiedSupplyChainConsignment.SpecifiedTradeDeliveryTerms,
                    DeliveryTypeCode: value
                  }
                }
              });
            },
            deliveryTermsCodes
          )}
          
          {/* Transbordo permitido */}
          <div className="mb-4">
            <label className="flex items-center text-gray-700 text-sm font-bold">
              <input
                type="checkbox"
                checked={formData.SpecifiedSupplyChainConsignment.TransshipmentPermissionIndicator || false}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    SpecifiedSupplyChainConsignment: {
                      ...formData.SpecifiedSupplyChainConsignment,
                      TransshipmentPermissionIndicator: e.target.checked
                    }
                  });
                }}
                className="mr-2"
              />
              Transbordo permitido
            </label>
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex items-center justify-between mt-6">
          <button
            type="submit"
            disabled={disabled}
            className={`font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              disabled 
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-700 text-white'
            }`}
          >
            {disabled ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Guardando...
              </>
            ) : (
              'Guardar'
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className={`font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              disabled 
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                : 'bg-gray-500 hover:bg-gray-700 text-white'
            }`}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default ECMRForm;
